import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import type { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import { selectCanDelete, selectHoodTypes, selectProjectAirflowUnit } from '../../store/project-data/project-data.selectors';
import { selectSessionLocale } from 'src/app/auth/store/session/session.selectors';
import { CopyHoodToProjectService } from '../../services/copy-hood-to-project.service';
import type { EntityType } from '../../../core/models/entity-type.model';
import type { Entity, TreeEntity } from '../../../core/models/entity.model';
import { EntityTypesActions } from '../../../core/store/entity-types/entity-types.actions';
import type { MeasurementUnit } from '../../../core/models/measurement-unit.model';
import { MeasurementUnitsActions } from '../../../core/store/measurement-units/measurement-units.actions';
import { selectEntityTypes } from '../../../core/store/entity-types/entity-types.selectors';
import { ConfirmDeleteDialogComponent } from '../../../project-list/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ProductMessagesService, ProductMessageStatuses } from '../../services/product-messages.service';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ProductListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectEntity: TreeEntity | null;

  canDeleteHood$ = (id: string) => this.store.select(selectCanDelete(id));
  uiLocale$: Observable<string>;
  hoodTypes$: Observable<EntityType[]>;

  id = '';
  pageIndex = 0;
  pageSize = 5;
  pageSizeOptions = [5, 10, 25, 50];

  displayedColumns = [
    'entityName',
    'entitySupplyAirFlow',
    'entityExtractAirFlow',
    'entityDateModified',
    'entityDateCreated',
    'entityIncludeInExports',
    'entityVersion',
    'entityCopyAction',
    'entityDeleteAction',
    'entityEditAction'
  ];

  dataSource = new EntityDataSource();

  airflowMeasurementUnit$: Observable<MeasurementUnit>;

  private entityTypes: Record<string, EntityType>;
  private entityTypesSubscription: Subscription;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private productMessagesService: ProductMessagesService,
    private copyHoodService: CopyHoodToProjectService,
    private dialog: MatDialog
  ) {
    this.id = this.route.snapshot.paramMap.get('id');
    this.canDeleteHood$ = (id: string) => this.store.select(selectCanDelete(id));
    this.uiLocale$ = this.store.select(selectSessionLocale);
    this.hoodTypes$ = this.store.select(selectHoodTypes);
    this.airflowMeasurementUnit$ = this.store.select(selectProjectAirflowUnit);
    this.entityTypesSubscription = this.store.select(selectEntityTypes).subscribe(types => {
      if (types) {
        this.entityTypes = types;
        this.updateDataSource();
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(EntityTypesActions.entity_types_required());
    this.store.dispatch(MeasurementUnitsActions.measurement_units_needed());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectEntity']) {
      this.updateDataSource();
    }
  }

  ngOnDestroy(): void {
    this.entityTypesSubscription?.unsubscribe();
  }

  updateDataSource() {
    /*
     * Table shows all first level children except messages
     */
    const productsArray =
      (this.projectEntity &&
        this.projectEntity.Children &&
        this.projectEntity.Children.filter(child => child.EntityTypeKey !== 'Message')) ??
      [];
    const productsPage = productsArray.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize);

    const productsWithMessageLevels = productsPage.map<IEntityAndMessageLevels>(entity => {
      const messageLevels = this.productMessagesService.getProductMessageStatuses(entity, this.entityTypes);
      return { entity, messageLevels };
    });

    this.dataSource.updateData(productsWithMessageLevels);
  }

  onRowClick(entity: Entity): void {
    this.router.navigate(['./products'], { queryParams: { selectedId: entity.Id }, relativeTo: this.route });
  }

  onCopyClick(entity: Entity): void {
    this.copyHoodService.copyToProject(entity);
  }

  onDeleteClick(entity: Entity): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, { data: entity });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(ProjectDataActions.delete_entity_start({ entityIdList: [entity.Id] }));
      }
    });
  }

  onEditClick(entity: Entity): void {
    this.router.navigate(['./products'], { queryParams: { selectedId: entity.Id }, relativeTo: this.route });
  }

  handlePageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateDataSource();
  }

  toggleHoodExport(event: MatCheckboxChange, hoodId: string) {
    this.store.dispatch(
      ProjectDataActions.update_project_start({
        entityId: hoodId,
        propertyKey: 'IncludeInExports',
        newValue: event.checked,
        updateFullProject: true
      })
    );
  }

  protected trackByEntity(_: number, item: IEntityAndMessageLevels): string {
    return item.entity.Id;
  }
}

interface IEntityAndMessageLevels {
  messageLevels: ProductMessageStatuses;
  entity: Entity;
}

export class EntityDataSource implements DataSource<IEntityAndMessageLevels> {
  private entitiesSubject = new BehaviorSubject<IEntityAndMessageLevels[]>([]);

  connect(collectionViewer: CollectionViewer): Observable<IEntityAndMessageLevels[] | readonly IEntityAndMessageLevels[]> {
    return this.entitiesSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.entitiesSubject.complete();
  }

  updateData(entities: IEntityAndMessageLevels[]): void {
    this.entitiesSubject.next(entities);
  }
}
